import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Package, CurrencyDollar, ChartBar, Clock, CheckCircle, Lock,
  CreditCard, Bank, Money, Truck, Receipt
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';

const PAYMENT_METHOD_META = {
  sinpe:          { label: 'SINPE',           color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Bank },
  paypal:         { label: 'PayPal',          color: 'bg-blue-50 text-blue-700 border-blue-200',         icon: CreditCard },
  cash_to_driver: { label: 'Cash to Driver',  color: 'bg-amber-50 text-amber-700 border-amber-200',      icon: Money      },
  cc_to_driver:   { label: 'CC to Driver',    color: 'bg-purple-50 text-purple-700 border-purple-200',   icon: CreditCard },
  direct_deposit: { label: 'Direct Deposit',  color: 'bg-sky-50 text-sky-700 border-sky-200',            icon: Bank },
  stripe:         { label: 'Stripe',          color: 'bg-violet-50 text-violet-700 border-violet-200',   icon: CreditCard },
  other:          { label: 'Other',           color: 'bg-gray-50 text-gray-700 border-gray-200',         icon: Receipt    },
  unspecified:    { label: 'Unspecified',     color: 'bg-gray-50 text-gray-500 border-gray-200',         icon: Receipt    },
};

const TABS = (canViewParentCarrier) => [
  { id: 'pending', label: 'Pending (Arrived SJ)', icon: Clock },
  { id: 'paid',    label: 'Paid Revenue',          icon: CheckCircle },
  ...(canViewParentCarrier ? [{ id: 'westy', label: 'ShipCR Logistics', icon: Lock }] : []),
];

export default function AccountingPage() {
  const { user } = useAuth();
  const { tx } = useLanguage();
  const canViewParentCarrier = !!(user && (user.role === 'super-admin' || user.role === 'super_admin' ||
    ['greg@trackmypackage.app', 'greg@mrpositivity.life'].includes((user.email || '').toLowerCase())));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [invoicing, setInvoicing] = useState(null); // pkg being marked paid
  const [paymentMethod, setPaymentMethod] = useState('sinpe');

  const fetchData = async () => {
    try {
      const res = await api.get('/accounting/summary');
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
  }, []);

  const handleQuickInvoiceAndPay = async (pkg) => {
    try {
      // Create invoice for this single package, then immediately mark paid with payment method
      const invRes = await api.post('/invoices', {
        customer_id_or_email: pkg.id, // not used — backend uses package_ids
        package_ids: [pkg.id],
      }).catch(() => null);
      let invoiceId = invRes?.data?.id;
      if (!invoiceId) {
        // fallback: use send-invoice endpoint to generate, then look up
        const r = await api.post(`/packages/${pkg.id}/send-invoice`);
        invoiceId = r.data?.invoice_id;
      }
      if (!invoiceId) throw new Error('Could not create invoice');
      await api.put(`/invoices/${invoiceId}/status`, null, { params: { status: 'paid', payment_method: paymentMethod } });
      toast.success(`Marked paid via ${PAYMENT_METHOD_META[paymentMethod].label}`);
      setInvoicing(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed');
    }
  };

  const handleSettleWestyBox = async () => {
    if (!data?.westy_box || data.westy_box.total_owed_usd === 0) {
      toast.error('Nothing to settle');
      return;
    }
    const ok = window.confirm(
      `Mark $${data.westy_box.total_owed_usd.toFixed(2)} (${data.westy_box.total_weight_kg} kg) as paid to ShipCR Logistics?\n\nThis will snapshot the current bill into history and reset the running total. Existing packages will be marked as settled.`
    );
    if (!ok) return;
    try {
      const label = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
      await api.post('/accounting/westy-box/settle', null, { params: { period_label: label } });
      toast.success(`ShipCR Logistics settled for ${label}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to settle');
    }
  };

  if (loading) {
    return (
      <Layout title="Accounting">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Accounting">
      <div className="space-y-6 premium-fade-up" data-testid="accounting-page">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="kpi-card p-5">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={18} className="text-amber-500" />
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Pending Payment</p>
            </div>
            <p className="text-3xl font-bold text-amber-600" data-testid="kpi-pending-total">${data?.pending?.total_usd?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-gray-500 mt-1">{data?.pending?.count || 0} packages · {data?.pending?.total_kg?.toFixed(2) || '0'} kg</p>
          </div>
          <div className="kpi-card p-5">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle size={18} className="text-green-500" />
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Paid Revenue</p>
            </div>
            <p className="text-3xl font-bold text-green-600" data-testid="kpi-paid-total">${data?.paid?.total_usd?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-gray-500 mt-1">{data?.paid?.count || 0} invoices paid</p>
          </div>
          {canViewParentCarrier && data?.westy_box && (
            <div className="kpi-card p-5 border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white" data-testid="westy-box-kpi">
              <div className="flex items-center gap-2 mb-1">
                <Lock size={16} className="text-rose-500" weight="fill" />
                <p className="text-xs uppercase tracking-wide text-rose-600 font-bold">Owed to ShipCR Logistics</p>
              </div>
              <p className="text-3xl font-bold text-rose-600">${data.westy_box.total_owed_usd?.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">{data.westy_box.total_weight_kg?.toFixed(2)} kg unsettled</p>
            </div>
          )}
          {canViewParentCarrier && data?.profit && (
            <div className="kpi-card p-5 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="profit-kpi">
              <div className="flex items-center gap-2 mb-1">
                <CurrencyDollar size={18} className="text-emerald-600" weight="bold" />
                <p className="text-xs uppercase tracking-wide text-emerald-700 font-bold">Net Profit (collected)</p>
              </div>
              <p className="text-3xl font-bold text-emerald-600">${data.profit.net_profit_collected_usd?.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">
                ${data.profit.gross_collected_usd?.toFixed(2)} in − ${data.profit.westy_box_total_cost_usd?.toFixed(2)} ShipCR Logistics
              </p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          {TABS(canViewParentCarrier).map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
                tab === t.id ? 'border-[#0077FF] text-[#0077FF]' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`accounting-tab-${t.id}`}
            >
              <t.icon size={16} weight={tab === t.id ? 'fill' : 'regular'} />
              {t.label}
            </button>
          ))}
        </div>

        {/* PENDING TAB */}
        {tab === 'pending' && (
          <div className="space-y-4">
            {/* Awaiting Collection — invoices client picked SINPE/PayPal/COD/CC-on-delivery */}
            {(data?.pending?.awaiting_collection || []).length > 0 && (
              <div className="kpi-card overflow-hidden border-2 border-amber-200 bg-amber-50/30">
                <div className="px-5 py-3 border-b border-amber-100 bg-gradient-to-r from-amber-50 to-white">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-amber-700 flex items-center gap-2">
                    <Clock size={16} weight="fill" /> Awaiting Collection — clients picked a payment method
                  </h3>
                  <p className="text-[11px] text-gray-500 mt-0.5">Quick-mark these as paid when the money arrives (SINPE/PayPal/Cash/Card from driver)</p>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-amber-50/50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="text-left py-2 px-4">Invoice</th>
                      <th className="text-left py-2 px-4">Customer</th>
                      <th className="text-left py-2 px-4">Client picked</th>
                      <th className="text-right py-2 px-4">USD</th>
                      <th className="text-right py-2 px-4">CRC</th>
                      <th className="text-center py-2 px-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pending.awaiting_collection.map(inv => {
                      const methodMap = {
                        sinpe: { label: 'SINPE Móvil', settle: 'sinpe' },
                        paypal: { label: 'PayPal', settle: 'paypal' },
                        cash_on_delivery: { label: 'Cash on Delivery', settle: 'cash_to_driver' },
                        card_on_delivery: { label: 'CC on Delivery', settle: 'cc_to_driver' },
                      };
                      const m = methodMap[inv.client_payment_choice] || { label: inv.client_payment_choice, settle: 'other' };
                      return (
                        <tr key={inv.id} className="border-t border-amber-100" data-testid={`awaiting-row-${inv.id}`}>
                          <td className="py-2 px-4 font-mono text-xs">{inv.invoice_number}</td>
                          <td className="py-2 px-4">{inv.customer_name}</td>
                          <td className="py-2 px-4">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border bg-amber-100/60 border-amber-200 text-amber-800 text-[11px] font-medium">
                              {m.label}
                            </span>
                          </td>
                          <td className="py-2 px-4 text-right font-bold">${inv.total_usd.toFixed(2)}</td>
                          <td className="py-2 px-4 text-right text-[#FF7A00] font-semibold">₡{inv.total_crc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="py-2 px-4 text-center">
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  await api.put(`/invoices/${inv.id}/status`, null, { params: { status: 'paid', payment_method: m.settle } });
                                  toast.success(`Marked collected via ${m.label}`);
                                  fetchData();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed');
                                }
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-md"
                              data-testid={`mark-collected-${inv.id}`}
                            >
                              <CheckCircle size={12} weight="bold" className="mr-1" />
                              Mark Collected
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="kpi-card overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">Packages waiting for invoice</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Payment method on Mark Paid:</span>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="text-xs rounded-md border border-gray-300 px-2 py-1"
                    data-testid="default-payment-method-select"
                  >
                    {Object.entries(PAYMENT_METHOD_META).filter(([k]) => k !== 'unspecified').map(([k, m]) => (
                      <option key={k} value={k}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left py-2 px-4">Tracking</th>
                  <th className="text-left py-2 px-4">Customer</th>
                  <th className="text-right py-2 px-4">Weight</th>
                  <th className="text-right py-2 px-4">Shipping</th>
                  <th className="text-right py-2 px-4">Transport</th>
                  <th className="text-right py-2 px-4">Total</th>
                  <th className="text-center py-2 px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {(data?.pending?.packages || []).map(p => (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`pending-row-${p.id}`}>
                    <td className="py-2 px-4 font-mono text-xs">{p.tracking_number}</td>
                    <td className="py-2 px-4">{p.customer_name}</td>
                    <td className="py-2 px-4 text-right">{p.weight_kg?.toFixed(2)} kg</td>
                    <td className="py-2 px-4 text-right">${p.shipping_cost?.toFixed(2)}</td>
                    <td className="py-2 px-4 text-right text-gray-500">${p.transport_fee?.toFixed(2) || '0.00'}</td>
                    <td className="py-2 px-4 text-right font-bold text-[#0077FF]">${p.total?.toFixed(2)}</td>
                    <td className="py-2 px-4 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleQuickInvoiceAndPay(p)}
                        className="text-xs rounded-md"
                        data-testid={`mark-paid-${p.id}`}
                      >
                        Invoice & Mark Paid
                      </Button>
                    </td>
                  </tr>
                ))}
                {(data?.pending?.packages || []).length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-gray-400 text-sm">No pending packages</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* PAID TAB */}
        {tab === 'paid' && (
          <div className="space-y-4">
            {/* Breakdown by method */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {Object.entries(data?.paid?.by_method || {}).filter(([_, v]) => v.count > 0).map(([method, v]) => {
                const meta = PAYMENT_METHOD_META[method] || PAYMENT_METHOD_META.other;
                const Icon = meta.icon;
                return (
                  <div key={method} className={`rounded-xl p-3 border ${meta.color}`} data-testid={`method-card-${method}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon size={14} weight="fill" />
                      <span className="text-[11px] uppercase tracking-wide font-bold">{meta.label}</span>
                    </div>
                    <p className="text-lg font-bold">${v.total_usd?.toFixed(2)}</p>
                    <p className="text-[10px] opacity-75">{v.count} invoice{v.count !== 1 ? 's' : ''}</p>
                  </div>
                );
              })}
              {(!data?.paid?.count) && (
                <div className="col-span-full text-center py-6 text-gray-400 text-sm">No paid invoices yet</div>
              )}
            </div>

            {/* Recent paid table */}
            {(data?.paid?.recent || []).length > 0 && (
              <div className="kpi-card overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">Recent paid invoices</h3>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="text-left py-2 px-4">Invoice</th>
                      <th className="text-left py-2 px-4">Customer</th>
                      <th className="text-left py-2 px-4">Method</th>
                      <th className="text-right py-2 px-4">Amount</th>
                      <th className="text-right py-2 px-4">Paid at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.paid.recent.map(r => {
                      const meta = PAYMENT_METHOD_META[r.payment_method] || PAYMENT_METHOD_META.other;
                      return (
                        <tr key={r.invoice_number} className="border-t border-gray-100" data-testid={`paid-row-${r.invoice_number}`}>
                          <td className="py-2 px-4 font-mono text-xs">{r.invoice_number}</td>
                          <td className="py-2 px-4">{r.customer_name}</td>
                          <td className="py-2 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium ${meta.color}`}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="py-2 px-4 text-right font-bold text-green-600">${r.total_usd?.toFixed(2)}</td>
                          <td className="py-2 px-4 text-right text-xs text-gray-500">{r.paid_at ? new Date(r.paid_at).toLocaleString() : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* PARENT-CARRIER (ShipCR Logistics) TAB — super-admin only */}
        {tab === 'westy' && canViewParentCarrier && data?.westy_box && (
          <div className="space-y-4">
            {/* Profit summary card */}
            {data.profit && (
              <div className="kpi-card p-5 bg-gradient-to-br from-emerald-50 via-white to-rose-50 border-2 border-emerald-200">
                <h3 className="text-sm font-bold uppercase tracking-wide text-emerald-700 mb-3 flex items-center gap-2">
                  <CurrencyDollar size={18} weight="fill" /> Profit Summary (Owner Only)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-[10px] uppercase text-gray-500 tracking-wider">Revenue Billed</p>
                    <p className="text-xl font-bold text-gray-900">${data.profit.revenue_billed_usd?.toFixed(2)}</p>
                    <p className="text-[10px] text-gray-400">across all handled pkgs</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-500 tracking-wider">Cash Collected</p>
                    <p className="text-xl font-bold text-green-600">${data.profit.gross_collected_usd?.toFixed(2)}</p>
                    <p className="text-[10px] text-gray-400">paid invoices</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-500 tracking-wider">ShipCR Logistics Cost</p>
                    <p className="text-xl font-bold text-rose-600">−${data.profit.westy_box_total_cost_usd?.toFixed(2)}</p>
                    <p className="text-[10px] text-gray-400">{data.profit.total_weight_handled_kg} kg total</p>
                  </div>
                  <div className="border-l-2 border-emerald-300 pl-3">
                    <p className="text-[10px] uppercase text-emerald-700 tracking-wider font-bold">Net Profit (Cash)</p>
                    <p className="text-2xl font-bold text-emerald-600">${data.profit.net_profit_collected_usd?.toFixed(2)}</p>
                    <p className="text-[10px] text-gray-500">≈ ${data.profit.profit_per_kg_usd?.toFixed(2)}/kg margin</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-emerald-100 grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-gray-500">ShipCR Logistics paid so far:</span>{' '}
                    <span className="font-semibold text-gray-700">${data.profit.westy_box_paid_usd?.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Still owed to ShipCR Logistics:</span>{' '}
                    <span className="font-semibold text-rose-600">${data.profit.westy_box_owed_usd?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Current bill + Settle button */}
            <div className="kpi-card overflow-hidden border-2 border-rose-100">
              <div className="px-5 py-4 border-b border-rose-100 bg-gradient-to-r from-rose-50 to-white flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wide text-rose-700 flex items-center gap-2">
                    <Lock size={16} weight="fill" /> ShipCR Logistics — Current Bill
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Unsettled packages × ${data.westy_box.rate_per_kg_usd}/kg · Settled to date:{' '}
                    <span className="font-semibold text-gray-700">{data.westy_box.settled_kg_to_date} kg · ${data.westy_box.settled_total_usd_to_date}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-[10px] uppercase text-gray-500">Total owed</p>
                    <p className="text-2xl font-bold text-rose-600">${data.westy_box.total_owed_usd?.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">{data.westy_box.total_weight_kg?.toFixed(2)} kg</p>
                  </div>
                  <Button
                    onClick={handleSettleWestyBox}
                    disabled={!data.westy_box.total_owed_usd}
                    className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg whitespace-nowrap"
                    data-testid="settle-westy-box-btn"
                  >
                    <CheckCircle size={16} weight="bold" className="mr-1.5" />
                    Mark Paid to ShipCR Logistics
                  </Button>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left py-2 px-4">Tracking</th>
                    <th className="text-left py-2 px-4">Customer</th>
                    <th className="text-left py-2 px-4">Status</th>
                    <th className="text-right py-2 px-4">Weight</th>
                    <th className="text-right py-2 px-4">ShipCR Logistics cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.westy_box.packages.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">All packages settled with ShipCR Logistics ✓</td></tr>
                  ) : data.westy_box.packages.map(p => (
                    <tr key={p.tracking_number} className="border-t border-gray-100">
                      <td className="py-2 px-4 font-mono text-xs">{p.tracking_number}</td>
                      <td className="py-2 px-4">{p.customer_name}</td>
                      <td className="py-2 px-4 text-xs text-gray-500">{p.status}</td>
                      <td className="py-2 px-4 text-right">{p.weight_kg?.toFixed(2)} kg</td>
                      <td className="py-2 px-4 text-right font-bold text-rose-600">${p.westy_box_cost_usd?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* History */}
            {data.westy_box.history?.length > 0 && (
              <div className="kpi-card overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                  <Receipt size={16} className="text-gray-500" />
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">ShipCR Logistics Payment History</h3>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="text-left py-2 px-4">Period</th>
                      <th className="text-right py-2 px-4">Packages</th>
                      <th className="text-right py-2 px-4">Weight</th>
                      <th className="text-right py-2 px-4">Amount</th>
                      <th className="text-right py-2 px-4">Settled at</th>
                      <th className="text-left py-2 px-4">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.westy_box.history.map(h => (
                      <tr key={h.id} className="border-t border-gray-100" data-testid={`westy-history-${h.id}`}>
                        <td className="py-2 px-4 font-medium">{h.period_label}</td>
                        <td className="py-2 px-4 text-right">{h.package_count}</td>
                        <td className="py-2 px-4 text-right">{h.total_kg?.toFixed(2)} kg</td>
                        <td className="py-2 px-4 text-right font-bold text-rose-600">${h.amount_usd?.toFixed(2)}</td>
                        <td className="py-2 px-4 text-right text-xs text-gray-500">{h.settled_at ? new Date(h.settled_at).toLocaleString() : '—'}</td>
                        <td className="py-2 px-4 text-xs text-gray-600">{h.settled_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
